import { AxiosError } from 'axios';
import { upperCase } from 'lodash';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { ContextMenuAction } from 'react-native-context-menu-view';

import { useDeleteTransactionIndividual, useRefundTransactionNPO } from '@/api/hooks/paymentAccountsHooks';
import { Transaction } from '@/api/models/paymentAccountsModels';
import { warningIcon } from '@/assets/images/icons';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useInfoModal } from '@/contexts/infoModalContext';
import { formatCurrency } from '@/utils/currencyFormat';
import { formatDayTime } from '@/utils/dateFormats';
import { getTransactionStatusColor } from '@/utils/getTransactionStatusColor';
import { i18n } from '@/utils/i18n';
import { paymentTypeToShow } from '@/utils/paymentTypeFormat';

import DotsLoader from '../DotsLoader';
import ContactNpoForm from '../forms/ContactNpoForm';
import ContextMenu from '../messages/ContextMenu.web';
import ConfirmWarningModal from '../modals/ConfirmWarningModal';
import Text from '../Text';

interface TransactionItemViewProps {
  transactionItem: Transaction;
  amountColor: string;
  isNpo: boolean;
  onDelete?: () => void;
}
const TransactionItemView = ({ transactionItem: item, amountColor, isNpo, onDelete }: TransactionItemViewProps) => {
  const { showSuccess, showError } = useEndFlowModal();
  const { dispatch, showInfo } = useInfoModal();
  const { mutateAsync: deleteTransactionIndividual, isPending: isPendingDelete } = useDeleteTransactionIndividual();
  const { mutateAsync: refundTransactionNpo, isPending: isPendingRefund } = useRefundTransactionNPO();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string | undefined>();

  const actions: ContextMenuAction[] = [
    {
      title: isNpo ? 'Refund Transaction' : 'Cancel Transaction',
      iconColor: 'red',
      icon: isNpo ? 'repeat' : 'delete'
    }
  ];

  const handleVoidRefund = () => {
    if (isNpo) {
      refundTransactionNpo(item.transactionId, {
        onSuccess: () => {
          showSuccess({
            title: i18n.t('end_flow.success_title'),
            message: i18n.t('donation.transaction_refund')
          });
          onDelete && onDelete();
        },
        onError: (e) => {
          console.error(e);
        }
      });
    } else {
      deleteTransactionIndividual(item.transactionId, {
        onSuccess: () => {
          showSuccess({
            title: i18n.t('end_flow.success_title'),
            message: i18n.t('donation.transaction_canceled')
          });
          onDelete && onDelete();
        },
        onError: (e: AxiosError) => {
          if (e.response?.status === 400) {
            showInfo({
              type: 'full',
              icon: warningIcon,
              title: i18n.t('donation.cancel_period_expired'),
              message: i18n.t('donation.cancel_period_message'),
              showDoneButton: false,
              accessoryView: (
                <ContactNpoForm
                  transactionId={item.transactionId}
                  npoName={item.nonProfitOrgs.name}
                  onDone={() => {
                    dispatch({ type: 'SET_VISIBLE', setVisible: false });
                  }}
                />
              )
            });
          } else {
            showError({});
          }
        }
      });
    }
    setShowConfirmationModal(false);
  };

  const amount = !isNpo && item.surcharge ? item.amount + item.surcharge : item.amount;
  const confirmLabel = isNpo ? i18n.t('donation.refund') : undefined;

  return (
    <View style={styles.mainContainer}>
      <View>
        <Text fontWeight="900" style={styles.title}>
          {isNpo ? item.donorUser.fullName : item.nonProfitOrgs.name}
        </Text>
        <View style={styles.infoContainer}>
          <Text>{`${i18n.t('donation.status')}: `}</Text>
          <Text fontWeight="900" style={{ color: getTransactionStatusColor(item.status) }}>
            {upperCase(item.status)}
          </Text>
        </View>
        <View style={styles.infoContainer}>
          <Text>{`${i18n.t('donation.payment_method')}: `}</Text>
          <Text>{paymentTypeToShow(item.paymentMethod)}</Text>
        </View>
        <Text>{formatDayTime(item.createdAt)}</Text>
        {item.surcharge !== undefined && isNpo && (
          <Text>{i18n.t('donation.npo_contributed_transaction_fee')}</Text>
          // <Text>{i18n.t('donation.transaction_fee', { FEE: item.surcharge?.toString() ?? '-' })}</Text>
        )}
        {item.notes !== null && <Text fontWeight="700">{i18n.t('donation.notes', { NOTES: item.notes })}</Text>}
      </View>
      <View style={styles.amountMenuContainer}>
        {(isPendingDelete || isPendingRefund) && <DotsLoader />}
        <Text style={{ color: amountColor }}>{formatCurrency(amount)}</Text>
        <ContextMenu
          disabled={false}
          longPressable={false}
          actions={actions}
          showMenuPadding={1.5}
          onPress={(actionTitle) => {
            switch (actionTitle) {
              case 'Refund Transaction': {
                setConfirmationMessage(i18n.t('donation.refund_confirm_message'));
                break;
              }
              case 'Cancel Transaction': {
                setConfirmationMessage(i18n.t('donation.cancel_confirm_message'));
                break;
              }
              default:
                setConfirmationMessage(undefined);
                break;
            }
            setShowConfirmationModal(true);
          }}>
          <View style={styles.dotsContainer}>
            <View style={styles.dot} />
            <View style={styles.dot} />
            <View style={styles.dot} />
          </View>
        </ContextMenu>
      </View>
      <ConfirmWarningModal
        message={confirmationMessage}
        confirmLabel={confirmLabel}
        showPicker={showConfirmationModal}
        loading={isPendingRefund || isPendingDelete}
        onClosePicker={() => setShowConfirmationModal(false)}
        onConfirmTapped={handleVoidRefund}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 18
  },

  title: {
    color: 'black'
  },

  amountMenuContainer: {
    flexDirection: 'row',
    gap: 15,
    alignItems: 'center'
  },

  dotsContainer: {
    flexDirection: 'row',
    gap: 5,
    padding: 5
  },

  dot: {
    width: 5,
    height: 5,
    borderRadius: 5 / 2,
    backgroundColor: 'black'
  },

  infoContainer: {
    flexDirection: 'row',
    gap: 3
  }
});

export default TransactionItemView;
